import type { AiCapabilityPage } from "../schema.js";
import { LIVE, OWNER_ADMIN } from "./_shared.js";

/**
 * `IDENTITY-LINK` (@UC0215) — trang **redirect** (US-AIV2-0049): chat KHÔNG hỏi ô, KHÔNG có nút Đồng ý,
 * chỉ một câu giải thích + nút mở đúng màn hình `/groups/:groupId/members` (ADR-0009 bất biến 5).
 * US-AIV2-0110 S10 (`@UC0259`): trước trỏ trang chủ nhóm `/groups/:groupId`; nay tab "Thành viên" — màn chi tiết thành viên cần `memberId` mà engine chưa điền được.
 * Cửa mobile trang này chịu trách nhiệm (gắn / gỡ tài khoản vào suất + chọn danh tính lúc nhận mời + tra người dùng). *
 * 🔴 `redirect` là QĐ CÓ CHỦ Ý, không phải "chưa kịp làm": `GET /users/search` + `GET /users/lookup` là API
 * TÌM TOÀN HỆ THỐNG, dựng riêng để CHỐNG QUÉT DANH BẠ (rate-limit + audit-on-read). Mở nó cho LLM là biến
 * nó thành kênh duyệt danh bạ bằng câu chữ, trong một app tài chính có trẻ em — QĐ người dùng 24/08/2026 đã
 * từ chối đúng việc đó một lần (`ai-coverage-map.json` cũ, trục "chọn danh tính"). Trang này GIỮ nguyên
 * phán quyết ấy và làm nó **đọc được**: nay nó là một trang có tên trong Sổ, không phải một dòng lý do trong
 * bảng tay mà không ai mở ra xem.
 */
export const IDENTITY_LINK = {
  id: "IDENTITY-LINK",
  uc: "@UC0215",
  tier: "redirect",
  status: LIVE,
  roles: OWNER_ADMIN, // US-AIV2-tourguide-0008 (@UC0362 · review #1238 H1): thủ quỹ/thành viên KHÔNG thấy việc này ở màn đích (cờ app `canManageMembers` / sửa đợt = chủ nhóm + quản trị) ⇒ hẹp vai cho đúng app.
  flags: [],
  slots: [],
  llm: {
    description: "Gắn một tài khoản đăng nhập vào đúng suất trong nhóm, hoặc gỡ gắn nhầm — trả lời câu hỏi “ai là ai”.",
    examples: [
      "ai là ai trong nhóm",
      "ai la ai trong nhom",
      "gắn tài khoản vào suất",
      "gan tai khoan vao suat",
      "nối tài khoản với tên trong danh sách",
      "hai người trùng tên",
      "hai nguoi trung ten",
      "gỡ tài khoản khỏi suất",
      "go tai khoan khoi suat",
      "link tài khoản",
      "chọn danh tính",
      // ——— 0105: thêm cho đủ 40 ———
      "nối tài khoản của tôi vào đúng suất",
      "noi tai khoan cua toi vao dung suat",
      "gắn nhầm người rồi, gỡ ra giúp mình",
      "gan nham nguoi roi go ra giup minh",
      "suất này là của ai vậy",
      "suat nay la cua ai vay",
      "tài khoản nào đang gắn với suất của Lan",
      "tai khoan nao dang gan voi suat cua lan",
      "ghép tài khoản với tên trong danh sách",
      "ghep tai khoan voi ten trong danh sach",
      "xác định ai ứng với suất nào",
      "xac dinh ai ung voi suat nao",
      "trong nhóm có hai bạn tên Minh, phân biệt kiểu gì",
      "trong nhom co hai ban ten minh phan biet kieu gi",
      "gắn danh tính cho phụ huynh mới vào",
      "gan danh tinh cho phu huynh moi vao",
      "suất của tôi chưa có tài khoản nào",
      "suat cua toi chua co tai khoan nao",
      "gỡ liên kết tài khoản của một suất",
      "go lien ket tai khoan cua mot suat",
      "ai đang dùng suất này",
      "ai dang dung suat nay",
      "nối danh tính đăng nhập với thành viên",
      "noi danh tinh dang nhap voi thanh vien",
      "cho tôi xem suất nào gắn tài khoản nào",
      "cho toi xem suat nao gan tai khoan nao",
      "nhận suất của mình trong nhóm",
      "nhan suat cua minh trong nhom",
      "liên kết người dùng với suất",
    ],
  },
  screen: "/groups/:groupId/members",
  screenShows: "identity_link",
  endpoints: [
    "GET /groups/{groupId}/members/{memberId}/accounts",
    "POST /groups/{groupId}/members/{memberId}/accounts",
    "DELETE /groups/{groupId}/members/{memberId}/accounts/{userId}",
    "POST /groups/{groupId}/invitations/identity-selection",
    "POST /groups/{groupId}/invitations/identity-selection/options",
    // QĐ người dùng 27/10/2026 (@UC9516) — chọn nhiều suất một lượt (màn nhận lời mời nhóm phụ huynh, không phải chat).
    "POST /groups/{groupId}/invitations/identity-selection/batch",
    "GET /users/search",
    "GET /users/lookup",
  ],
  facts: [],
  templates: {
    /*
     * 🔴 GUIDE-SCREEN-TRUTH (G-1, @UC0215): nút thêm người đại diện CHỈ hiện ở nhóm phụ huynh (`member_basic_info_section.dart`
     * `_groupHasRepresentatives`); nhóm thường mỗi người tự nhận chỗ đã giữ qua link mời rồi được duyệt (`invite_seat_claim_subtitle`); suất
     * mang vai quản lý thì gắn/gỡ bị chặn 409 (QĐ 12/10, lối ra: chủ nhóm đổi vai về Thành viên trước). Không có cơ chế câu theo loại nhóm cho
     * trang redirect ⇒ MỘT câu đúng cho cả hai loại.
     */
    redirect:
      "Mình mở danh sách thành viên nhé. Ở nhóm phụ huynh, bấm vào tên trong danh sách rồi bấm nút thêm phụ huynh để nối tài khoản; nhóm khác thì mỗi người tự nhận đúng chỗ khi vào nhóm bằng link mời, bạn chỉ cần duyệt. Suất đang mang vai quản lý thì chủ nhóm đổi vai về Thành viên trước. Chọn bằng mắt nhé — gắn nhầm người là tiền của người này chạy sang sổ người kia.",
  },
  journey: { after: ["READ-PEOPLE-MINE", "NAME-IN-GROUP"] },
  help: { title: "Mở danh sách thành viên", tryNow: "ai là ai trong nhóm" },
} satisfies AiCapabilityPage;
